import React, {useContext, useEffect, useState} from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import TextInput from "connectui/component/input/text/type/TextInput";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import {useDispatch, useSelector} from "react-redux";
import {appendMessage, editMessages} from "redux/actions/messagesActions";
import {createAction} from "redux/actions/experiencesActions";
import {
  closeGeneralError,
  openGeneralError,
} from "redux/actions/settingsActions";
import {MessagesContext} from "../../panel/conversations/ConversationsPanel";

const useStyles = (theme) =>
  buildStyles({
    noteInput: {
      flex: 1,
      width: "100%",
      height: "100%",
      padding: theme.spacing(0, 4),
      "& textarea::placeholder": {
        color: theme.colors.warning400,
      },
    },
    noteButton: {
      width: "20px",
      height: "20px",
      padding: theme.spacing(0.5),
      color: theme.colors.white,
    },
  });

const NoteInput = ({guestId, setNoteText = () => {}}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const {setNotes} = useContext(MessagesContext);
  const dispatch = useDispatch();
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [inputText, setInputText] = useState("");
  const inputProps = isMobile
    ? {
        style: {
          width: "100%",
          minHeight: "24px",
          maxHeight: "120px",
          resize: "none",
          color: theme.colors.error900,
          backgroundColor: theme.colors.warning200,
          cursor: "text",
          ...theme.typography.textXS.regular,
          "&::placeholder": {color: theme.colors.warning400},
        },
        onInput: (e) => {
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        },
      }
    : {
        style: {
          width: "100%",
          height: "100%",
          resize: "none",
          color: theme.colors.error900,
          backgroundColor: theme.colors.warning200,
          cursor: "text",
          ...theme.typography.textXS.regular,
          "&::placeholder": {color: theme.colors.warning400},
        },
      };

  useEffect(() => {
    setNoteText(inputText);
  }, [inputText]);

  const createNote = () => {
    const sent = Date.now();
    const body = {
      enso_user_id: user_profile.enso_user_id,
      guest_id: guestId,
      channel: "note",
      message: inputText,
      enso_key: user_profile.enso_key,
      sender_type: "user",
      sent,
    };

    // Optimistic loading: Add the note to the conversation
    const newLocalMessage = {...body, id: sent};
    dispatch(appendMessage(guestId, newLocalMessage));
    setNotes((prev) => [...prev, newLocalMessage]);

    const action = {
      action_id: "create_message",
      guest_id: guestId,
      body,
    };

    dispatch(
      createAction({
        action,
        guestId,
        disableAlerts: true,
        onError: () => {
          dispatch(
            openGeneralError({
              open: true,
              message: "Failed to send message",
              disableSeverity: true,
              subtitle:
                "There was a problem creating your note, please try again later",
              onClose: () => dispatch(closeGeneralError()),
            }),
          );
          dispatch(
            editMessages({...newLocalMessage, sending_error: true}, sent),
          );
        },
      }),
    );
    setInputText("");
  };

  const input = (
    <TextInput
      as={"textarea"}
      className={classes.noteInput}
      inputProps={{
        value: inputText,
        onChange: (e) => setInputText(e.target.value),
        placeholder: "Type your note",
        ...inputProps,
      }}
    />
  );

  if (isMobile) return input;

  return (
    <FlexContainer
      flexDirection="column"
      alignItems="flex-end"
      gap={theme.spacing(1)}
      sx={{
        height: "100%",
        padding: theme.spacing(4, 0),
        backgroundColor: theme.colors.warning200,
      }}
    >
      {input}
      <ButtonIconText
        label={"Create Note"}
        weight="semibold"
        typographyVariant="textSM"
        color="white"
        sx={{
          backgroundColor: theme.colors.warning900,
          gap: theme.spacing(2),
          marginRight: theme.spacing(4),
          "&:hover": {
            backgroundColor: theme.colors.warning800,
          },
        }}
        onClick={createNote}
        disabled={!inputText?.trim()}
        icon={<SvgIcon component={ICONS.note} className={classes.noteButton} />}
      />
    </FlexContainer>
  );
};

export default NoteInput;
